import { apiClient } from "../api/apiClient.js";

/**
 * P167. Thin fetch wrappers over the Partner Invite Card API
 * (BackofficePartnerInviteCardsController): the Super Agent's own invite letter
 * to a licensed overseas recruitment agency, plus the read-back of the agencies
 * that answered it.
 *
 * Every route here is scoped by a Super Agent ORGANIZATION id - never an Agent
 * id. That is the whole difference from ../agent/inviteCardsApi.js (worker
 * intake under an Agent) and ../agent/employerInviteCardsApi.js (employer
 * onboarding under an Agent), and it is why this is its own module rather than
 * three more functions in one of those: a mixed module is how the wrong id ends
 * up in the path.
 */

const PARTNER_PATH = "/backoffice/super-agents";

function requireSuperAgentId(superAgentOrganizationId, caller) {
  if (!superAgentOrganizationId) {
    throw new Error(`${caller} requires a superAgentOrganizationId`);
  }
  return encodeURIComponent(superAgentOrganizationId);
}

// Exactly the query params BackofficePartnerInviteCardsController
// .ListPartnerInviteCards declares, in the same order - anything else passed in
// is silently dropped rather than forwarded.
const LIST_PARAM_KEYS = ["page", "pageSize", "status", "partnerCountryCode", "search"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`). */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listPartnerInviteCards(superAgentOrganizationId, params = {}) {
  const id = requireSuperAgentId(superAgentOrganizationId, "listPartnerInviteCards");
  return apiClient.get(`${PARTNER_PATH}/${id}/partner-invite-cards${buildQuery(LIST_PARAM_KEYS, params)}`);
}

/**
 * partnerCountryCode is required by the API: the letter IS the country, and
 * there is no neutral fallback letter to fall back on.
 */
export function createPartnerInviteCard(
  superAgentOrganizationId,
  { partnerCountryCode, intendedLanguageCode, noteToPartner, maxUses, expiresAtUtc } = {}
) {
  const id = requireSuperAgentId(superAgentOrganizationId, "createPartnerInviteCard");
  return apiClient.post(`${PARTNER_PATH}/${id}/partner-invite-cards`, {
    partnerCountryCode: partnerCountryCode || null,
    intendedLanguageCode: intendedLanguageCode || null,
    noteToPartner: noteToPartner || null,
    // Omitted (null) means UNLIMITED - the API's own contract. Never send 0.
    maxUses: maxUses ?? null,
    expiresAtUtc: expiresAtUtc || null,
  });
}

export function getPartnerInviteCard(superAgentOrganizationId, partnerInviteCardId) {
  const id = requireSuperAgentId(superAgentOrganizationId, "getPartnerInviteCard");
  return apiClient.get(`${PARTNER_PATH}/${id}/partner-invite-cards/${encodeURIComponent(partnerInviteCardId)}`);
}

export function revokePartnerInviteCard(superAgentOrganizationId, partnerInviteCardId) {
  const id = requireSuperAgentId(superAgentOrganizationId, "revokePartnerInviteCard");
  return apiClient.post(`${PARTNER_PATH}/${id}/partner-invite-cards/${encodeURIComponent(partnerInviteCardId)}/revoke`, {});
}

export function listPartnerApplications(superAgentOrganizationId, params = {}) {
  const id = requireSuperAgentId(superAgentOrganizationId, "listPartnerApplications");
  return apiClient.get(`${PARTNER_PATH}/${id}/partner-applications${buildQuery(["search", "page", "pageSize"], params)}`);
}
